import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import EnvoiCourriel from './EnvoiCourriel';
import Layout from './Layout';
import { enregistrerSinistre, lireReclamation } from './api';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

function Section({ titre, children }) {
  return (
    <Box sx={{ mb: 4, breakInside: 'avoid' }}>
      <Typography variant="h3" sx={{ mb: 1.5 }}>{titre}</Typography>
      {children}
    </Box>
  );
}

/** Le message proposé : ce qu'on écrirait soi-même, à corriger au besoin. */
function messageType(donnees, declaration) {
  const { identification: id, circonstances: c } = donnees;
  const reperes = [
    id.police && `police ${id.police}`,
    c.date && `sinistre du ${dateCourte(c.date)}`,
  ].filter(Boolean).join(', ');
  const corps = declaration
    ? `Veuillez trouver ci-joint ma déclaration de sinistre${reperes ? ` (${reperes})` : ''}.

Les pièces disponibles à ce jour sont accessibles par le lien ci-dessous ; celles qui manquent vous seront transmises dès que possible.`
    : `Veuillez trouver ci-joint ma réclamation${reperes ? ` (${reperes})` : ''}, accompagnée des pièces justificatives accessibles par le lien ci-dessous.`;
  return `Madame, Monsieur,

${corps}

Je reste à votre disposition pour tout renseignement complémentaire.

Cordialement,
${id.assure || ''}`.trim();
}

/**
 * L'envoi, depuis la plateforme.
 *
 * Le PDF est produit au moment d'envoyer, joint au courriel et rangé dans le
 * dossier ; les pièces partent par lien. L'expéditeur reçoit une copie : il
 * garde sa preuve dans sa messagerie, comme s'il avait envoyé lui-même. Sa
 * propre messagerie reste possible, en second.
 */
function Envoi({ expertiseId, donnees, declaration, sur, onEnvoye, onEnvoyeMoiMeme,
                 onSuivi }) {
  const [parti, setParti] = useState(null);
  const envoi = donnees.envoi;
  if (!envoi) return null;

  const avec = envoi.destinataires.filter((d) => d.email && d.role !== 'Vous');
  const principal = avec.filter((d) => !d.copie).map((d) => d.email).join(',');
  const copies = avec.filter((d) => d.copie).map((d) => d.email).join(',');
  const lien = `mailto:${principal}`
    + `?cc=${encodeURIComponent(copies)}`
    + `&subject=${encodeURIComponent(envoi.objet)}`;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }} className="sans-impression">
      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Envoyer {declaration ? 'cette déclaration' : 'cette réclamation'}
      </Typography>

      {sur && !parti && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {declaration ? 'Déclarée' : 'Envoyée'} le {dateCourte(sur)}. Vous pouvez
          renvoyer une version à jour ; chaque envoi est gardé dans le suivi.
        </Alert>
      )}

      {parti ? (
        <Alert severity="success"
               action={onSuivi && (
                 <Button color="inherit" size="small" onClick={onSuivi}>
                   Voir le suivi
                 </Button>
               )}>
          {parti}
        </Alert>
      ) : (
        <EnvoiCourriel
          expertiseId={expertiseId}
          document={declaration ? 'declaration' : 'reclamation'}
          proposes={envoi.destinataires}
          objetPropose={envoi.objet}
          messagePropose={messageType(donnees, declaration)}
          piecesJointesParDefaut
          libelleBouton={declaration ? 'Envoyer ma déclaration' : 'Envoyer ma réclamation'}
          onEnvoye={(reponse) => { setParti(reponse.detail); onEnvoye(); }} />
      )}

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Vous préférez envoyer depuis votre propre messagerie ? Enregistrez le
          document en PDF (bouton en bas de page), joignez-le à votre courriel
          avec vos pièces, puis revenez l'indiquer.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" component="a" href={lien}
                  disabled={!principal && !copies}>
            Ouvrir mon courriel pré-rempli
          </Button>
          {!sur && (
            <Button size="small" onClick={onEnvoyeMoiMeme}>
              Je l'ai envoyée moi-même
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

function Ligne({ gauche, droite, gras }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" sx={{ fontWeight: gras ? 600 : 400 }}>
        {gauche}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: gras ? 600 : 400,
                                        whiteSpace: 'nowrap' }}>
        {droite}
      </Typography>
    </Stack>
  );
}

/**
 * La réclamation, telle qu'elle part chez l'assureur.
 *
 * Elle n'ajoute rien à ce que l'assuré a déclaré : elle l'ordonne. Et elle
 * tait une chose — le registre des hypothèses, où l'on note ce qui n'est pas
 * établi. Il reste dans le dossier de l'assuré ; le montrer à la compagnie
 * avant sa réponse reviendrait à lui livrer les points faibles.
 */
export default function Reclamation({ expertiseId, onRetour, onSuivi,
                                      mode = 'reclamation' }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [envoye, setEnvoye] = useState(null);

  const declaration = mode === 'declaration';

  useEffect(() => {
    let vivant = true;
    lireReclamation(expertiseId, mode)
      .then((d) => vivant && setDonnees(d))
      .catch(() => vivant && setErreur('Impossible de composer le document.'));
    return () => { vivant = false; };
  }, [expertiseId, mode]);

  const marquerEnvoye = async () => {
    const aujourdhui = new Date().toISOString().slice(0, 10);
    try {
      await enregistrerSinistre(expertiseId, { declare_le: aujourdhui });
      setEnvoye(aujourdhui);
    } catch {
      setErreur("La date d'envoi n'a pas pu être enregistrée.");
    }
  };

  if (erreur) {
    return (
      <Layout titre={declaration ? 'Votre déclaration' : 'Votre réclamation'}>
        <Alert severity="error">{erreur}</Alert>
      </Layout>
    );
  }
  if (!donnees) {
    return (
      <Layout titre={declaration ? 'Votre déclaration' : 'Votre réclamation'}>
        <CircularProgress size={28} />
      </Layout>
    );
  }

  const { identification: id, circonstances: c } = donnees;
  const totaux = donnees.totaux || {};
  const manquantes = donnees.pieces.attendues.filter((p) => p.obligatoire);

  return (
    <Layout
      titre={declaration ? 'Votre déclaration de sinistre' : 'Votre réclamation'}
      introduction={declaration
        ? "À envoyer sans attendre : le délai court dès le sinistre. Ni la cause exacte ni les devis ne sont nécessaires — ce qui manque est annoncé."
        : "Voici votre dossier tel qu'il sera présenté. Rien n'y est ajouté : tout vient de ce que vous avez déclaré."}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large"
                  onClick={() => window.print()}>
            Imprimer ou enregistrer en PDF
          </Button>
        </>
      }
    >
      <Envoi expertiseId={expertiseId} donnees={donnees} declaration={declaration}
             sur={envoye || (declaration ? donnees.envoi?.declare_le : null)}
             onEnvoye={() => setEnvoye(new Date().toISOString().slice(0, 10))}
             onEnvoyeMoiMeme={marquerEnvoye}
             onSuivi={onSuivi} />

      {declaration && !id.police && (
        <Alert severity="warning" sx={{ mb: 3 }} className="sans-impression">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Le numéro de police manque.
          </Typography>
          <Typography variant="body2">
            Sans lui, votre assureur ne peut pas rattacher cette déclaration à
            un contrat, et le délai continue de courir. Renseignez-le dans
            « Votre contrat » avant d'envoyer.
          </Typography>
        </Alert>
      )}

      {!declaration && manquantes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} className="sans-impression">
          Avant d'envoyer : {manquantes.map((p) => p.libelle).join(', ')}.
          Sans {manquantes.length > 1 ? 'ces pièces' : 'cette pièce'}, votre
          réclamation se discute au lieu de s'imposer.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Section titre="Le dossier">
          <Stack spacing={0.5}>
            <Ligne gauche="Assuré" droite={id.assure || '—'} />
            {id.intermediaire && (
              <Ligne gauche="Intermédiaire" droite={id.intermediaire} />
            )}
            <Ligne gauche="Compagnie" droite={id.compagnie || '—'} />
            {id.police && <Ligne gauche="Numéro de police" droite={id.police} />}
            {id.numero_client && (
              <Ligne gauche="Numéro de client" droite={id.numero_client} />
            )}
            {id.conditions_generales && (
              <Ligne gauche="Conditions générales"
                     droite={id.conditions_generales} />
            )}
            {id.reference && (
              <Ligne gauche="Référence du dossier" droite={id.reference} />
            )}
          </Stack>
        </Section>

        <Section titre="Les circonstances">
          <Stack spacing={1}>
            {c.nature && <Ligne gauche="Nature" droite={c.nature} />}
            {c.date && <Ligne gauche="Date" droite={dateCourte(c.date)} />}
            {c.adresse && <Ligne gauche="Lieu" droite={c.adresse} />}
            {c.recit && (
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {c.recit}
              </Typography>
            )}
            {c.precisions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {c.precisions.map((p) => (
                  <Ligne key={p.question} gauche={p.question} droite={p.reponse} />
                ))}
              </Box>
            )}
          </Stack>
        </Section>

        {declaration && donnees.dommages.length > 0 && (
          <Section titre="Les dommages constatés">
            <Stack spacing={1}>
              {donnees.dommages.map((d, i) => (
                <Box key={i}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.element}{d.piece ? ` — ${d.piece}` : ''}
                    {d.surface ? ` · ${d.surface} m²` : ''}
                  </Typography>
                  {d.description && (
                    <Typography variant="body2">{d.description}</Typography>
                  )}
                  {d.photos > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {d.photos} photo{d.photos > 1 ? 's' : ''} jointe
                      {d.photos > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Section>
        )}

        {donnees.en_attente?.length > 0 && (
          <Section titre="Ce qui vous sera transmis">
            <Stack spacing={0.5}>
              {donnees.en_attente.map((a, i) => (
                <Typography key={i} variant="body2">· {a}</Typography>
              ))}
            </Stack>
          </Section>
        )}

        {donnees.travaux?.length > 0 && (
          <Section titre="Les travaux">
            {donnees.travaux.map((poste) => (
              <Box key={poste.titre} sx={{ mb: 2 }}>
                <Ligne
                  gauche={poste.titre}
                  droite={euros(poste.reclame)}
                  gras
                />
                {poste.provenance === 'bareme' && (
                  <Typography variant="caption" color="text.secondary">
                    Montant calculé sur le barème de référence
                    {poste.fourchette
                      && ` — fourchette ${euros(poste.fourchette[0])} à ${euros(poste.fourchette[1])}`}
                  </Typography>
                )}
                {poste.provenance === 'devis' && poste.calcule > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Sur base de votre devis — le barème calcule
                    {' '}{euros(poste.calcule)}
                  </Typography>
                )}
                {poste.lignes.length > 0 && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Poste</TableCell>
                          <TableCell align="right">Quantité</TableCell>
                          <TableCell align="right">Prix unitaire</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {poste.lignes.map((l, i) => (
                          <TableRow key={`${l.code}-${i}`}>
                            <TableCell>{l.libelle}</TableCell>
                            <TableCell align="right">
                              {Number(l.quantite)} {l.unite}
                            </TableCell>
                            <TableCell align="right">
                              {euros(l.prix_unitaire)}
                            </TableCell>
                            <TableCell align="right">{euros(l.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            ))}
          </Section>
        )}

        {donnees.contenu?.length > 0 && (
          <Section titre="Le contenu">
            <Stack spacing={0.5}>
              {donnees.contenu.map((o) => (
                <Ligne
                  key={o.objet}
                  gauche={`${o.objet}${o.achete_le ? ` — acheté le ${dateCourte(o.achete_le)}` : ''}`}
                  droite={euros(o.reclame)}
                />
              ))}
            </Stack>
          </Section>
        )}

        {donnees.frais?.length > 0 && (
          <Section titre="Les autres frais">
            <Stack spacing={0.5}>
              {donnees.frais.map((f) => (
                <Ligne key={f.titre} gauche={f.titre} droite={euros(f.reclame)} />
              ))}
            </Stack>
          </Section>
        )}

        {!declaration && <Divider sx={{ my: 3 }} />}

        {!declaration && (
        <Stack spacing={0.5} sx={{ mb: 4 }}>
          {totaux.travaux > 0 && (
            <Ligne gauche="Travaux" droite={euros(totaux.travaux)} />
          )}
          {totaux.contenu > 0 && (
            <Ligne gauche="Contenu" droite={euros(totaux.contenu)} />
          )}
          {totaux.frais > 0 && (
            <Ligne gauche="Autres frais" droite={euros(totaux.frais)} />
          )}
          <Divider sx={{ my: 1 }} />
          <Ligne gauche="Total réclamé, hors TVA"
                 droite={euros(totaux.general)} gras />
        </Stack>
        )}

        {donnees.points?.length > 0 && (
          <Section titre="Les points invoqués">
            <Stack spacing={2}>
              {donnees.points.map((p) => (
                <Box key={p.titre} sx={{ breakInside: 'avoid' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {p.titre}
                    {p.garantie ? ` — ${p.garantie}` : ''}
                  </Typography>
                  {p.faits && (
                    <Typography variant="body2">{p.faits}</Typography>
                  )}
                  {p.fondement_contractuel && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {p.fondement_contractuel}
                    </Typography>
                  )}
                  {p.calcul && (
                    <Typography variant="body2" color="text.secondary"
                                sx={{ mt: 0.5 }}>
                      {p.calcul}
                    </Typography>
                  )}
                  {p.consequence && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {p.consequence}
                    </Typography>
                  )}
                  {p.citations.map((cit, i) => (
                    <Typography key={i} variant="caption" component="div"
                                color="text.secondary" sx={{ mt: 0.5 }}>
                      {cit.source} — « {cit.extrait} »
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          </Section>
        )}

        {donnees.pieces.jointes.length > 0 && (
          <Section titre="Les pièces jointes">
            <Stack spacing={0.25} sx={{ mb: 2 }}>
              {donnees.pieces.jointes.map((p, i) => (
                <Typography key={i} variant="body2">
                  {i + 1}. {p.titre || 'Sans titre'}
                  {p.categorie ? ` (${p.categorie})` : ''}
                </Typography>
              ))}
            </Stack>

            {/* Une photo décrite ne vaut pas une photo montrée : l'assureur
                doit voir le dommage sans ouvrir une autre pièce jointe. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {donnees.pieces.jointes
                .filter((p) => p.est_image && p.url)
                .map((p, i) => (
                  <Box key={i} sx={{ width: 220, breakInside: 'avoid' }}>
                    <Box component="img" src={p.url} alt={p.titre || 'Pièce'}
                         sx={{ width: '100%', border: '1px solid',
                               borderColor: 'divider', borderRadius: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {p.titre || 'Sans titre'}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Section>
        )}
      </Paper>
    </Layout>
  );
}
